// Helpers to interpret AuditLog payloads. Two shapes coexist:
//  - new entries: { request, response } (response captured by the backend
//    interceptor — for DELETEs it is the only record of WHAT was affected)
//  - legacy entries: the request body stored directly at the top level

export interface PayloadSections {
  request: unknown;
  response: unknown;
}

export const getPayloadSections = (
  payload: object | null
): PayloadSections => {
  if (!payload || typeof payload !== "object" || Array.isArray(payload)) {
    return { request: payload ?? null, response: null };
  }
  const record = payload as Record<string, unknown>;
  if ("request" in record || "response" in record) {
    return {
      request: record.request ?? null,
      response: record.response ?? null,
    };
  }
  return { request: payload, response: null };
};

/** True when the value carries information worth rendering (not null/{}/[]). */
export const hasContent = (value: unknown): boolean => {
  if (value === null || value === undefined) return false;
  if (value instanceof Date) return true;
  if (typeof value === "object") return Object.keys(value as object).length > 0;
  return true;
};

/**
 * Derives a human-readable label for the affected entity from the captured
 * response (title for content, full name or email for users). Returns null
 * for legacy entries or responses without a recognizable field.
 */
export const getEntityLabel = (payload: object | null): string | null => {
  const { response } = getPayloadSections(payload);
  if (!response || typeof response !== "object" || Array.isArray(response)) {
    return null;
  }
  const record = response as Record<string, unknown>;
  const fullName = [record.first_name, record.last_name]
    .filter((part) => typeof part === "string" && part.trim() !== "")
    .join(" ");
  const candidates = [record.title, fullName || undefined, record.name, record.email];
  const label = candidates.find(
    (candidate) => typeof candidate === "string" && candidate.trim() !== ""
  );
  return (label as string) ?? null;
};
